import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../config';
import '../styles/components/adminDashboard.css';

interface AdminStats {
  user_count: number;
  item_count: number;
  queue_total: number;
  queue_pending: number;
  queue_processing: number;
  queue_completed: number;
  queue_failed: number;
}

interface RecentBatch {
  id: number;
  batch_id: string;
  status: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  processing_time?: number;
  error_message?: string;
}

const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const [adminStats, setAdminStats] = useState<AdminStats>({ 
    user_count: 0, 
    item_count: 0,
    queue_total: 0,
    queue_pending: 0,
    queue_processing: 0,
    queue_completed: 0,
    queue_failed: 0
  });
  const [recentBatches, setRecentBatches] = useState<RecentBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const prevBatchesRef = useRef<string>('');

  const fetchAdminStats = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setAdminStats(data);
      } else {
        console.error('Admin stats error:', resp.status, await resp.text());
      }
    } catch (e) {
      console.error('Failed to fetch admin stats:', e);
    }
  };

  const fetchRecentBatches = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/queue/recent`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        const newData = JSON.stringify(data.batches || []);
        // Only update if data changed to prevent flashing
        if (newData !== prevBatchesRef.current) {
          setRecentBatches(data.batches || []);
          prevBatchesRef.current = newData;
        }
      }
    } catch (e) {
      console.error('Failed to fetch recent batches:', e);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (loading) {
        await Promise.all([fetchAdminStats(), fetchRecentBatches()]);
        setLoading(false);
      } else {
        // Subsequent loads don't show loading state
        await Promise.all([fetchAdminStats(), fetchRecentBatches()]);
      }
    };
    loadData();
    // Refresh every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Update page summary in parent component
  useEffect(() => {
    const summaryElement = document.querySelector('.page-summary-text');
    if (summaryElement && adminStats.user_count > 0) {
      summaryElement.textContent = `${adminStats.user_count} users, ${adminStats.item_count} items`;
    }
  }, [adminStats]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'var(--color-success)';
      case 'failed': return 'var(--color-danger)';
      case 'processing': return 'var(--color-warning)';
      default: return 'var(--color-text-muted)';
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-content">
        {/* Queue Statistics - Compact */}
        <section className="dashboard-section">
          <h3>Queue Statistics</h3>
          <div className="queue-stats-grid">
            <div className="stat-box">
              <div className="stat-number">{adminStats.queue_total}</div>
              <div className="stat-label">Total</div>
            </div>
            <div className="stat-box pending">
              <div className="stat-number">{adminStats.queue_pending}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-box processing">
              <div className="stat-number">{adminStats.queue_processing}</div>
              <div className="stat-label">Processing</div>
            </div>
            <div className="stat-box completed">
              <div className="stat-number">{adminStats.queue_completed}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-box failed">
              <div className="stat-number">{adminStats.queue_failed}</div>
              <div className="stat-label">Failed</div>
            </div>
          </div>
        </section>

        {/* Recent Batches */}
        <section className="dashboard-section no-padding">
        <h3>Recent Batches</h3>
        <div className="queue-table-wrap">
          <table className="queue-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Started</th>
                <th>Time</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {recentBatches.map(batch => (
                <tr key={batch.id}>
                  <td>{batch.id}</td>
                  <td>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(batch.status) }}>
                      {batch.status}
                    </span>
                  </td>
                  <td>{batch.started_at ? new Date(batch.started_at).toLocaleString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true,
                    timeZoneName: 'short'
                  }) : '-'}</td>
                  <td>{batch.processing_time ? `${batch.processing_time}s` : '-'}</td>
                  <td>{batch.error_message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
        {/* iOS IAP Tools */}
        <section className="dashboard-section">
          <h3>iOS IAP Tools</h3>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
            Clears local purchase ownership tracking. Use before switching test accounts. Does not cancel subscriptions.
          </p>
          <button
            onClick={() => {
              localStorage.removeItem('iap_purchase_owner');
              localStorage.removeItem('iap_processed_transactions');
              alert('IAP state cleared. Restart the app before testing a purchase.');
            }}
            style={{ fontSize: '13px', padding: '10px 20px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
          >
            Reset IAP State
          </button>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
